import React, { useEffect, useState } from "react";
import { auth, db } from "../firebase";
import { doc, getDoc } from "firebase/firestore";
import { useAuthState } from "react-firebase-hooks/auth";
import { useNavigate } from "react-router-dom";
import { signOut } from "firebase/auth";
import { useFont } from "../context/FontContext";
import { motion } from "framer-motion";
import { CheckCircle, Circle, ArrowRight, BrainCircuit, Activity, Lock, LogOut } from "lucide-react";

export default function Dashboard() {
  const [user, loading] = useAuthState(auth);
  const navigate = useNavigate();
  const { fontSize } = useFont();

  // Test Durumları
  const [status, setStatus] = useState({
    disc: false,
    ocean: false,
    riasec: false
  });
  const [checking, setChecking] = useState(true);

  // Veritabanından Test Durumlarını Kontrol Et & LocalStorage Senkronizasyonu
  useEffect(() => {
    const checkTests = async () => {
      if (!user) return;
      try {
        // Testleri Tek Tek Kontrol Et
        const discSnap = await getDoc(doc(db, "users", user.uid, "answers", "disc"));
        const oceanSnap = await getDoc(doc(db, "users", user.uid, "answers", "ocean"));
        const riasecSnap = await getDoc(doc(db, "users", user.uid, "answers", "riasec"));

        // Eğer test veritabanında varsa, sonucunu tarayıcı hafızasına (LocalStorage) da kaydet.
        // Bu sayede Rapor sayfası hızlıca bulabilir.
        if (discSnap.exists()) localStorage.setItem('discResults', JSON.stringify(discSnap.data()));
        if (oceanSnap.exists()) localStorage.setItem('oceanResults', JSON.stringify(oceanSnap.data()));
        if (riasecSnap.exists()) localStorage.setItem('riasecResults', JSON.stringify(riasecSnap.data()));

        setStatus({
          disc: discSnap.exists(),
          ocean: oceanSnap.exists(),
          riasec: riasecSnap.exists()
        });
      } catch (error) {
        console.error("Veri çekme hatası:", error);
      } finally {
        setChecking(false);
      }
    };

    if (!loading) checkTests();
  }, [user, loading]);

  const handleLogout = async () => {
    try {
      localStorage.clear(); // Çıkışta verileri temizle
      await signOut(auth);
      navigate("/login");
    } catch (error) {
      console.error("Çıkış hatası:", error);
    }
  };

  // İlerleme Hesapla
  const completedCount = Object.values(status).filter(Boolean).length;
  const progress = (completedCount / 3) * 100;
  const allFinished = completedCount === 3;

  // --- MODERN STİLLER ---
  const styles = {
    container: { maxWidth: '1000px', margin: '40px auto', padding: '20px', fontFamily: 'Inter, sans-serif' },
    headerRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' },
    welcomeSection: { background: 'linear-gradient(135deg, #6c5ce7, #a29bfe)', padding: '40px', borderRadius: '20px', color: 'white', marginBottom: '40px', boxShadow: '0 10px 30px rgba(108, 92, 231, 0.3)' },
    grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px' },
    card: { background: '#fff', padding: '25px', borderRadius: '15px', boxShadow: '0 4px 15px rgba(0,0,0,0.05)', border: '1px solid #f0f0f0', transition: 'transform 0.2s', cursor: 'pointer', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' },
    progressBarContainer: { width: '100%', height: '10px', background: 'rgba(255,255,255,0.3)', borderRadius: '5px', marginTop: '15px', overflow: 'hidden' },
    progressBarFill: { height: '100%', background: '#00b894', width: `${progress}%`, transition: 'width 0.5s ease-in-out' },
    statusBadge: (done) => ({ padding: '5px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: 'bold', background: done ? '#d4edda' : '#fff3cd', color: done ? '#155724' : '#856404', display: 'inline-flex', alignItems: 'center', gap: '5px' }),
    logoutBtn: { background: '#ff7675', color: 'white', border: 'none', padding: '10px 20px', borderRadius: '8px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 'bold' }
  };

  if (loading || checking) return <div style={{textAlign:'center', marginTop:50}}>Yükleniyor...</div>;
  if (!user) return <div style={{textAlign:'center', marginTop:50}}>Lütfen giriş yapın.</div>;

  return (
    <div style={styles.container}>
      
      {/* Üst Bar (Çıkış Butonu) */}
      <div style={styles.headerRow}>
        <div></div> {/* Boşluk için */}
        <button onClick={handleLogout} style={styles.logoutBtn}>
          <LogOut size={18} /> Çıkış Yap
        </button>
      </div>

      {/* 1. HOŞGELDİN & İLERLEME ALANI */}
      <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} style={styles.welcomeSection}>
        <h1 style={{ fontSize: `${fontSize + 24}px`, fontWeight: '800', margin: 0 }}>
          Merhaba, {user?.displayName?.split(" ")[0] || "Gezgin"}! 👋
        </h1>
        <p style={{ fontSize: `${fontSize + 4}px`, opacity: 0.9, marginTop: '10px' }}>
          Geleceğini şekillendirmek için testleri tamamla.
        </p>
        
        <div style={{ marginTop: '25px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px', fontWeight: 'bold' }}>
            <span>Genel İlerleme</span>
            <span>%{Math.round(progress)}</span>
          </div>
          <div style={styles.progressBarContainer}>
            <div style={styles.progressBarFill}></div>
          </div>
        </div>
      </motion.div>

      {/* 2. TEST KARTLARI */}
      <h2 style={{ color: '#2d3436', marginBottom: '20px' }}>📋 Yetenek Testleri</h2>
      <div style={styles.grid}>
        
        {/* DISC TESTİ */}
        <motion.div 
          whileHover={{ y: -5 }}
          style={styles.card} 
          onClick={() => navigate(status.disc ? "/results/disc" : "/test/disc")}
        >
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '15px' }}>
              <div style={{ background: '#e17055', width: '50px', height: '50px', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white' }}>
                <Activity size={24} />
              </div>
              <div style={styles.statusBadge(status.disc)}>
                {status.disc ? <CheckCircle size={14}/> : <Circle size={14}/>}
                {status.disc ? "Tamamlandı" : "Başlanmadı"}
              </div>
            </div>
            <h3 style={{ fontSize: `${fontSize + 6}px`, color: '#2d3436', margin: '0 0 10px 0' }}>DISC Kişilik Envanteri</h3>
            <p style={{ color: '#636e72', fontSize: `${fontSize}px` }}>Davranış tarzını ve iletişim dilini analiz eder.</p>
          </div>
          <div style={{ marginTop: '20px', color: '#e17055', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '5px' }}>
            {status.disc ? "Sonuçları Gör" : "Teste Başla"} <ArrowRight size={16} />
          </div>
        </motion.div>

        {/* OCEAN TESTİ */}
        <motion.div 
          whileHover={{ y: -5 }}
          style={styles.card} 
          onClick={() => navigate(status.ocean ? "/results/ocean" : "/test/ocean")}
        >
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '15px' }}>
              <div style={{ background: '#0984e3', width: '50px', height: '50px', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white' }}>
                <BrainCircuit size={24} />
              </div>
              <div style={styles.statusBadge(status.ocean)}>
                {status.ocean ? <CheckCircle size={14}/> : <Circle size={14}/>}
                {status.ocean ? "Tamamlandı" : "Başlanmadı"}
              </div>
            </div>
            <h3 style={{ fontSize: `${fontSize + 6}px`, color: '#2d3436', margin: '0 0 10px 0' }}>Büyük Beşli (OCEAN)</h3>
            <p style={{ color: '#636e72', fontSize: `${fontSize}px` }}>Psikolojik dayanıklılık ve açıklık seviyeni ölçer.</p>
          </div>
          <div style={{ marginTop: '20px', color: '#0984e3', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '5px' }}>
            {status.ocean ? "Sonuçları Gör" : "Teste Başla"} <ArrowRight size={16} />
          </div>
        </motion.div>

        {/* RIASEC TESTİ */}
        <motion.div 
          whileHover={{ y: -5 }}
          style={styles.card} 
          onClick={() => navigate(status.riasec ? "/results/riasec" : "/test/riasec")}
        >
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '15px' }}>
              <div style={{ background: '#00b894', width: '50px', height: '50px', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white' }}>
                <Activity size={24} />
              </div>
              <div style={styles.statusBadge(status.riasec)}>
                {status.riasec ? <CheckCircle size={14}/> : <Circle size={14}/>}
                {status.riasec ? "Tamamlandı" : "Başlanmadı"}
              </div>
            </div>
            <h3 style={{ fontSize: `${fontSize + 6}px`, color: '#2d3436', margin: '0 0 10px 0' }}>RIASEC Mesleki İlgi</h3>
            <p style={{ color: '#636e72', fontSize: `${fontSize}px` }}>Hangi iş ortamlarının sana uygun olduğunu bulur.</p>
          </div>
          <div style={{ marginTop: '20px', color: '#00b894', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '5px' }}>
            {status.riasec ? "Sonuçları Gör" : "Teste Başla"} <ArrowRight size={16} />
          </div>
        </motion.div>

      </div>

      {/* 3. AI RAPOR KARTI (Kilitli/Açık) */}
      <h2 style={{ color: '#2d3436', margin: '40px 0 20px 0' }}>🚀 Sonuç ve Analiz</h2>
      <motion.div 
        whileHover={allFinished ? { scale: 1.02 } : {}}
        onClick={() => allFinished ? navigate("/ai-report") : alert("Lütfen önce tüm testleri tamamla!")}
        style={{
          ...styles.card, 
          background: allFinished ? 'linear-gradient(135deg, #2d3436, #000)' : '#f5f6fa',
          color: allFinished ? 'white' : '#b2bec3',
          border: 'none',
          flexDirection: 'row',
          alignItems: 'center',
          opacity: allFinished ? 1 : 0.7,
          minHeight: '120px'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
          <div style={{ background: allFinished ? '#fdcb6e' : '#dfe6e9', width: '60px', height: '60px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#2d3436' }}>
            {allFinished ? <BrainCircuit size={32} /> : <Lock size={30} />}
          </div>
          <div>
            <h3 style={{ fontSize: `${fontSize + 8}px`, margin: 0 }}>
              {allFinished ? "Yapay Zeka Raporunu Oluştur" : "Rapor Kilitli"}
            </h3>
            <p style={{ margin: '5px 0 0 0', opacity: 0.8, fontSize: `${fontSize}px` }}>
              {allFinished ? "Analiz hazır! Kariyer yolculuğunu keşfetmek için tıkla." : "Analizi başlatmak için yukarıdaki 3 testi tamamlamalısın."}
            </p>
          </div>
        </div>
        {allFinished && <ArrowRight size={32} />}
      </motion.div>

    </div>
  );
}